import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth";
import { ErrorBanner, Modal } from "../../components";
import {
  createIPAllowlistEntry,
  deleteIPAllowlistEntry,
  fetchIPAllowlist,
  listMerchants,
  setIPAllowlistEnforcement,
  updateIPAllowlistEntry,
} from "../../api";
import type { IPAllowlistItem, MerchantResponse } from "../../api";

const MERCHANT_LIST_LIMIT = 200;

function formatDate(raw: string): string {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat("ru-RU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function buildEntryPayload(
  ip: string,
  merchantId: string,
  comment: string,
): {
  ip: string;
  merchantId: string;
  comment: string | null;
} | null {
  const nextIP = ip.trim();
  const nextMerchantID = merchantId.trim();
  if (!nextIP || !nextMerchantID) return null;
  const nextComment = comment.trim();
  return {
    ip: nextIP,
    merchantId: nextMerchantID,
    comment: nextComment === "" ? null : nextComment,
  };
}

export function AdminIPAllowlistPage(): JSX.Element {
  const { token } = useAuth();

  const [items, setItems] = useState<IPAllowlistItem[]>([]);
  const [merchants, setMerchants] = useState<MerchantResponse[]>([]);
  const [enforced, setEnforced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState("");

  const [newIP, setNewIP] = useState("");
  const [newMerchantID, setNewMerchantID] = useState("");
  const [newComment, setNewComment] = useState("");
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);

  const [editTarget, setEditTarget] = useState<IPAllowlistItem | null>(null);
  const [editIP, setEditIP] = useState("");
  const [editMerchantID, setEditMerchantID] = useState("");
  const [editComment, setEditComment] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<unknown>(null);

  const [deleteTarget, setDeleteTarget] = useState<IPAllowlistItem | null>(
    null,
  );
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<unknown>(null);

  const [enforcementTarget, setEnforcementTarget] = useState<boolean | null>(
    null,
  );
  const [enforcementSaving, setEnforcementSaving] = useState(false);
  const [enforcementError, setEnforcementError] = useState<unknown>(null);

  const reload = useCallback((): void => {
    if (!token) return;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchIPAllowlist(token),
      listMerchants(token, { limit: MERCHANT_LIST_LIMIT }),
    ])
      .then(([allowlist, merchantResp]) => {
        setItems(allowlist.items);
        setEnforced(allowlist.enforced);
        setMerchants(merchantResp.items);
        // Functional update keeps newMerchantID out of the deps below:
        // with it in the closure, changing the select would re-create
        // reload and trigger a redundant refetch via the effect.
        setNewMerchantID((prev) => prev || merchantResp.items[0]?.id || "");
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    reload();
  }, [reload]);

  const merchantOptions = useMemo(
    () =>
      merchants.map((merchant) => ({ id: merchant.id, name: merchant.name })),
    [merchants],
  );

  const submitCreate = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!token) {
      setCreateError("Требуется повторный вход.");
      return;
    }
    const payload = buildEntryPayload(newIP, newMerchantID, newComment);
    if (!payload) {
      setCreateError("Заполните IP и мерчанта.");
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    createIPAllowlistEntry(token, payload)
      .then(() => {
        setNewIP("");
        setNewComment("");
        setSuccess("IP добавлен в белый список.");
        reload();
      })
      .catch((err) => setCreateError(err))
      .finally(() => setCreateSaving(false));
  };

  const openEdit = (item: IPAllowlistItem): void => {
    setEditTarget(item);
    setEditIP(item.ip);
    setEditMerchantID(item.merchantId);
    setEditComment(item.comment ?? "");
    setEditError(null);
  };

  const submitEdit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!token || !editTarget) return;
    const payload = buildEntryPayload(editIP, editMerchantID, editComment);
    if (!payload) {
      setEditError("Заполните IP и мерчанта.");
      return;
    }
    setEditSaving(true);
    setEditError(null);
    updateIPAllowlistEntry(token, editTarget.id, payload)
      .then(() => {
        setEditTarget(null);
        setSuccess("Запись белого списка обновлена.");
        reload();
      })
      .catch((err) => setEditError(err))
      .finally(() => setEditSaving(false));
  };

  const confirmDelete = (): void => {
    if (!token || !deleteTarget) return;
    const target = deleteTarget;
    setDeleteSaving(true);
    setDeleteError(null);
    deleteIPAllowlistEntry(token, target.id)
      .then(() => {
        setDeleteTarget(null);
        setSuccess("IP удалён из белого списка.");
        reload();
      })
      .catch((err) => setDeleteError(err))
      .finally(() => setDeleteSaving(false));
  };

  const confirmEnforcement = (): void => {
    if (!token || enforcementTarget === null) return;
    setEnforcementSaving(true);
    setEnforcementError(null);
    setIPAllowlistEnforcement(token, enforcementTarget)
      .then((resp) => {
        setEnforced(resp.enforced);
        setEnforcementTarget(null);
        setSuccess(
          resp.enforced ? "IP-фильтр включён." : "IP-фильтр выключен.",
        );
        reload();
      })
      .catch((err) => setEnforcementError(err))
      .finally(() => setEnforcementSaving(false));
  };

  return (
    <div className="page admin-ip-allowlist">
      <div className="page-header">
        <div>
          <h2>IP Allowlist</h2>
          <p className="page-subtitle">{items.length} addresses</p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            onClick={reload}
            disabled={loading}
          >
            {loading ? "Обновление..." : "Обновить"}
          </button>
        </div>
      </div>

      {success && (
        <div className="alert alert-info">
          {success}
          <button
            className="alert-dismiss"
            type="button"
            onClick={() => setSuccess("")}
          >
            ×
          </button>
        </div>
      )}
      <ErrorBanner error={error} title="Белый список IP не загружен" />

      <section className={`ip-allowlist-status ${enforced ? "active" : ""}`}>
        <div>
          <strong>
            {enforced
              ? "Фильтр включён — доступ только с адресов из списка"
              : "Фильтр выключен — API открыт для всех адресов"}
          </strong>
          <span>
            Проверка применяется к merchant Bearer API и legacy JWT-запросам
            роли merchant.
          </span>
        </div>
        <button
          className={
            enforced ? "btn btn-ghost btn-sm" : "btn btn-primary btn-sm"
          }
          type="button"
          onClick={() => {
            setEnforcementError(null);
            setEnforcementTarget(!enforced);
          }}
          disabled={loading}
        >
          {enforced ? "Выключить" : "Включить"}
        </button>
      </section>

      <div className="ip-allowlist-grid">
        <section className="ip-allowlist-form-section">
          <div className="section-header">
            <h3>Добавить IP</h3>
          </div>
          <form className="ip-allowlist-form" onSubmit={submitCreate}>
            <ErrorBanner error={createError} title="IP не добавлен" />
            <div className="form-group">
              <label htmlFor="allowlist-ip">IP или подсеть</label>
              <input
                id="allowlist-ip"
                type="text"
                value={newIP}
                onChange={(e) => setNewIP(e.target.value)}
                placeholder="1.2.3.4 или 10.0.0.0/24"
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label htmlFor="allowlist-merchant">Мерчант</label>
              <select
                id="allowlist-merchant"
                value={newMerchantID}
                onChange={(e) => setNewMerchantID(e.target.value)}
                disabled={merchantOptions.length === 0}
              >
                {merchantOptions.map((merchant) => (
                  <option key={merchant.id} value={merchant.id}>
                    {merchant.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="allowlist-comment">Комментарий</label>
              <textarea
                id="allowlist-comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                maxLength={256}
                rows={3}
              />
            </div>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={createSaving || merchantOptions.length === 0}
            >
              {createSaving ? "Добавление..." : "Добавить"}
            </button>
          </form>
        </section>

        <section className="ip-allowlist-table-section">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>IP</th>
                  <th>Мерчант</th>
                  <th>Комментарий</th>
                  <th>Создан</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="empty-state">
                      Загрузка белого списка...
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty-state">
                      Список пуст.
                    </td>
                  </tr>
                )}
                {!loading &&
                  items.map((item) => (
                    <tr key={item.id}>
                      <td className="mono ip-cell">{item.ip}</td>
                      <td>
                        <strong>{item.merchantName}</strong>
                        <div className="cell-subtle mono">
                          {item.merchantId}
                        </div>
                      </td>
                      <td className="ip-comment-cell">{item.comment || "—"}</td>
                      <td>{formatDate(item.createdAt)}</td>
                      <td className="merchant-action-cell">
                        <button
                          className="btn btn-ghost btn-sm"
                          type="button"
                          onClick={() => openEdit(item)}
                        >
                          Редактировать
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          type="button"
                          onClick={() => {
                            setDeleteError(null);
                            setDeleteTarget(item);
                          }}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Modal
        open={editTarget !== null}
        title="Редактировать IP"
        onClose={() => setEditTarget(null)}
        sizeClassName="modal-sm"
      >
        <form className="ip-allowlist-form" onSubmit={submitEdit}>
          <ErrorBanner error={editError} title="IP не обновлён" />
          <div className="form-group">
            <label htmlFor="edit-allowlist-ip">IP или подсеть</label>
            <input
              id="edit-allowlist-ip"
              type="text"
              value={editIP}
              onChange={(e) => setEditIP(e.target.value)}
              placeholder="1.2.3.4 или 10.0.0.0/24"
              autoComplete="off"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-allowlist-merchant">Мерчант</label>
            <select
              id="edit-allowlist-merchant"
              value={editMerchantID}
              onChange={(e) => setEditMerchantID(e.target.value)}
            >
              {merchantOptions.map((merchant) => (
                <option key={merchant.id} value={merchant.id}>
                  {merchant.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="edit-allowlist-comment">Комментарий</label>
            <textarea
              id="edit-allowlist-comment"
              value={editComment}
              onChange={(e) => setEditComment(e.target.value)}
              maxLength={256}
              rows={3}
            />
          </div>
          <div className="modal-actions">
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => setEditTarget(null)}
              disabled={editSaving}
            >
              Отмена
            </button>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={editSaving}
            >
              {editSaving ? "Сохранение..." : "Сохранить"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={deleteTarget !== null}
        title="Удалить IP"
        onClose={() => setDeleteTarget(null)}
        sizeClassName="modal-sm"
      >
        <ErrorBanner error={deleteError} title="IP не удалён" />
        {deleteTarget && (
          <p className="modal-hint">
            IP <strong>{deleteTarget.ip}</strong> будет удалён из общего белого
            списка.
          </p>
        )}
        <div className="modal-actions">
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => setDeleteTarget(null)}
            disabled={deleteSaving}
          >
            Отмена
          </button>
          <button
            className="btn btn-danger"
            type="button"
            onClick={confirmDelete}
            disabled={deleteSaving}
          >
            {deleteSaving ? "Удаление..." : "Удалить"}
          </button>
        </div>
      </Modal>

      <Modal
        open={enforcementTarget !== null}
        title={
          enforcementTarget ? "Включить IP-фильтр?" : "Выключить IP-фильтр?"
        }
        onClose={() => setEnforcementTarget(null)}
        sizeClassName="modal-sm"
      >
        <ErrorBanner error={enforcementError} title="Режим не изменён" />
        <p className="modal-hint">
          {enforcementTarget
            ? "После включения merchant API будет принимать запросы только с адресов из белого списка."
            : "После выключения merchant API снова будет принимать запросы с любых адресов."}
        </p>
        {enforcementTarget && items.length === 0 && (
          <div className="alert alert-error">
            <strong>
              Список пуст: будут заблокированы ВСЕ запросы мерчантов.
            </strong>
          </div>
        )}
        <div className="modal-actions">
          <button
            className="btn btn-ghost"
            type="button"
            onClick={() => setEnforcementTarget(null)}
            disabled={enforcementSaving}
          >
            Отмена
          </button>
          <button
            className={enforcementTarget ? "btn btn-danger" : "btn btn-primary"}
            type="button"
            onClick={confirmEnforcement}
            disabled={enforcementSaving}
          >
            {enforcementSaving ? "Сохранение..." : "Подтвердить"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
